/* Hallmark · genre: modern-minimal · macrostructure: centered-modal · design-system: local · designed-as-app */
import React, { useState, useEffect, useRef } from 'react';
import { API_URLS } from '../config/api';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { Check, BrainCircuit, AlertCircle } from 'lucide-react';

// --- CONFIG ---
const POLL_INTERVAL_MS = 1000;
const MAX_DURATION_MS = 90000;      // abandon après 90 s
const MAX_NOT_STARTED = 30;         // "not_started" consécutifs tolérés (upload en cours)
const MAX_NETWORK_FAILURES = 5;     // échecs réseau consécutifs tolérés
const PROGRESS_CAP = 92;            // plafond tant que le serveur n'a pas fini
const PROGRESS_DURATION = 60;       // durée (s) pour atteindre le plafond

const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const steps = [
  { id: 1, name: 'Numérisation', description: 'Lecture de la copie' },
  { id: 2, name: 'Extraction', description: 'Reconnaissance des maths' },
];

const STAGE_MESSAGES = [
  "Photo bien reçue…",
  "Lecture de ton écriture…",
  "Reconnaissance des maths…",
  "Dernières vérifications…",
];

const stageOf = (p) => (p < 18 ? 0 : p < 45 ? 1 : p < 72 ? 2 : 3);

// Courbe "rapide au début, lente ensuite" (équivalent 1 - e^-kt), normalisée 0 → 1
const easeProgress = (t) => (1 - Math.exp(-5 * t)) / (1 - Math.exp(-5));

const Loading = ({ analysisData, onStartTransition, onError, mode = 'analysis' }) => {
  const [stage, setStage] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [doneCount, setDoneCount] = useState(0);

  const pollingRef = useRef(null);
  const completedRef = useRef(false);
  const progressAnimRef = useRef(null);
  const percentTextRef = useRef(null);
  const stageRef = useRef(0);

  // Callbacks en ref : le polling ne redémarre pas à chaque render du parent
  const onStartTransitionRef = useRef(onStartTransition);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onStartTransitionRef.current = onStartTransition;
    onErrorRef.current = onError;
  }, [onStartTransition, onError]);

  const logoUrl = "https://res.cloudinary.com/do5zemcrk/image/upload/v1762868623/WhatsApp_Image_2025-11-11_at_14.25.45_bbfsgq.jpg";

  // --- PROGRESSION : une seule animation, zéro re-render React ---
  const progressMV = useMotionValue(0);
  const strokeDashoffset = useTransform(progressMV, (v) => CIRCUMFERENCE - (v / 100) * CIRCUMFERENCE);

  useEffect(() => {
    // Écrit le % directement dans le DOM + ne re-render que lors d'un changement d'étape
    const unsub = progressMV.on("change", (v) => {
      if (percentTextRef.current) percentTextRef.current.textContent = Math.round(v);
      const s = stageOf(v);
      if (s !== stageRef.current) {
        stageRef.current = s;
        setStage(s);
      }
    });

    const cap = mode === 'ocr' ? 90 : PROGRESS_CAP;
    progressAnimRef.current = animate(progressMV, cap, {
      duration: PROGRESS_DURATION,
      ease: easeProgress,
    });

    return () => {
      unsub();
      progressAnimRef.current?.stop();
    };
  }, [mode, progressMV]);

  const markCompleted = (count) => {
    if (completedRef.current) return;
    completedRef.current = true;
    progressAnimRef.current?.stop();
    animate(progressMV, 100, { duration: 0.5, ease: [0.22, 1, 0.36, 1] });
    setDoneCount(count);
    setIsCompleted(true);
    setTimeout(() => onStartTransitionRef.current?.(), 1000); // laisse voir l'animation de succès
  };

  // --- POLLING + GESTION D'ERREURS ---
  useEffect(() => {
    if (mode === 'ocr') return;

    // 1. Données déjà disponibles
    if (analysisData && analysisData.data && !completedRef.current) {
      markCompleted(Object.keys(analysisData.data.etape_1_student_solutions || {}).length);
      return;
    }
    if (completedRef.current) return;

    // 2. Sinon : polling
    let userId = null;
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('user_info') || '{}');
      userId = userInfo.id || (userInfo.user && userInfo.user.id);
    } catch (e) {
      userId = null;
    }

    const startedAt = Date.now();
    let failures = 0;
    let notStarted = 0;
    let finished = false;
    let inFlight = false;

    const stopPolling = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const fail = (msg) => {
      if (finished) return;
      finished = true;
      stopPolling();
      progressAnimRef.current?.stop();
      console.error('❌ Loading - échec:', msg);
      setHasFailed(true);
      setFailMessage(msg);
      setTimeout(() => onErrorRef.current?.(msg), 1800);
    };

    if (!userId) {
      fail("Session expirée. Reconnecte-toi.");
      return;
    }

    const checkExtraction = async () => {
      if (finished || inFlight) return;

      if (Date.now() - startedAt > MAX_DURATION_MS) {
        fail("L'analyse prend trop de temps. Réessaie.");
        return;
      }

      inFlight = true;
      try {
        const url = `${API_URLS.GET_PROGRESS(userId)}?t=${Date.now()}`;
        const response = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const progressData = await response.json();
        if (finished) return;
        failures = 0;

        if (progressData.status === 'error') {
          fail(progressData.message || "Une erreur est survenue. Réessaie.");
          return;
        }

        if (progressData.status === 'not_started') {
          notStarted += 1;
          if (notStarted > MAX_NOT_STARTED) {
            fail("L'analyse n'a pas démarré. Réessaie.");
            return;
          }
        } else {
          notStarted = 0;
        }

        const isOcrFinished =
          progressData.etape_1_student_solutions !== undefined &&
          progressData.etape_1_student_solutions !== null;

        if (isOcrFinished || progressData.status === 'completed') {
          finished = true;
          stopPolling();
          markCompleted(Object.keys(progressData.etape_1_student_solutions || {}).length);
        }
      } catch (error) {
        console.error('❌ Erreur polling:', error);
        failures += 1;
        if (failures >= MAX_NETWORK_FAILURES) fail("Problème de connexion. Réessaie.");
      } finally {
        inFlight = false;
      }
    };

    pollingRef.current = setInterval(checkExtraction, POLL_INTERVAL_MS);
    checkExtraction();

    return () => {
      finished = true;
      stopPolling();
    };
  }, [analysisData, mode]);

  // --- VALEURS DÉRIVÉES ---
  const stageMessage = hasFailed
    ? failMessage
    : isCompleted
      ? `Extraction terminée${doneCount ? ` (${doneCount} question${doneCount > 1 ? 's' : ''})` : ''}`
      : STAGE_MESSAGES[stage];

  const currentStep = isCompleted || stage >= 2 ? 2 : 1;

  const accent = hasFailed ? '#F87171' : isCompleted ? '#7C9374' : '#4A90E2';
  const accentEnd = hasFailed ? '#FCA5A5' : isCompleted ? '#A8C3A0' : '#8B7CF6';

  return (
    <div className="min-h-[100dvh] bg-[#F8F9FB] flex flex-col items-center justify-center p-4 sm:p-6 font-sans overflow-hidden relative">

      {/* Lueur statique (aucun blur, aucune animation : gratuit pour le GPU) */}
      <div
        className="fixed inset-0 pointer-events-none transition-colors duration-700"
        style={{ color: accent, background: 'radial-gradient(circle at 50% 38%, currentColor 0%, transparent 55%)', opacity: 0.1 }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={hasFailed ? { opacity: 1, scale: 1, y: 0, x: [0, -6, 6, -4, 4, 0] } : { opacity: 1, scale: 1, y: 0 }}
        transition={hasFailed ? { duration: 0.4 } : { type: "spring", damping: 26, stiffness: 280 }}
        className="w-full max-w-sm bg-white rounded-[32px] shadow-xl border border-slate-100 p-8 relative z-10 overflow-hidden"
      >
        {/* Header / Badge */}
        <div className="flex flex-col items-center justify-center mb-8">
          <div className="w-12 h-12 bg-white rounded-[14px] shadow-md border border-slate-100 flex items-center justify-center mb-3 overflow-hidden">
            <img src={logoUrl} alt="Logo" className="h-full w-full object-cover" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-50 border border-slate-200">
            {hasFailed ? (
              <AlertCircle className="w-3.5 h-3.5 text-red-500" />
            ) : (
              <BrainCircuit className={`w-3.5 h-3.5 ${isCompleted ? 'text-[#7C9374]' : 'text-[#4A90E2] animate-pulse'}`} />
            )}
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {hasFailed ? 'Erreur' : isCompleted ? 'Analyse Terminée' : 'IA en cours'}
            </span>
          </div>
        </div>

        {/* Cercle de progression */}
        <div className="flex flex-col items-center justify-center mb-10">
          <div className="relative w-40 h-40">

            {/* Un seul satellite en orbite (transform uniquement) */}
            {!isCompleted && !hasFailed && (
              <motion.div
                className="absolute inset-[-6px] will-change-transform"
                animate={{ rotate: 360 }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              >
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#4A90E2]" />
              </motion.div>
            )}

            <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
              <defs>
                <linearGradient id="spyrale-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={accent} />
                  <stop offset="100%" stopColor={accentEnd} />
                </linearGradient>
              </defs>
              <circle cx="60" cy="60" r={RADIUS} stroke="#F1F5F9" strokeWidth="8" fill="none" />
              <motion.circle
                cx="60" cy="60" r={RADIUS}
                stroke="url(#spyrale-ring)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                style={{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset }}
              />
            </svg>

            {/* Centre */}
            <div className="absolute inset-0 flex items-center justify-center">
              {hasFailed ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 18 }}>
                  <AlertCircle className="w-12 h-12 text-red-400" />
                </motion.div>
              ) : isCompleted ? (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 320, damping: 15 }}
                  className="w-16 h-16 rounded-full bg-[#7C9374] flex items-center justify-center">
                  <Check className="w-9 h-9 text-white" strokeWidth={3.5} />
                </motion.div>
              ) : (
                <span className="text-4xl font-black font-mono tracking-tighter text-[#2B4360] tabular-nums">
                  <span ref={percentTextRef}>0</span>
                  <span className="text-xl text-slate-400 ml-0.5">%</span>
                </span>
              )}
            </div>

            {/* Mini burst de succès : 8 particules, transform + opacity seulement */}
            {isCompleted && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                {Array.from({ length: 8 }).map((_, i) => {
                  const angle = (i / 8) * Math.PI * 2;
                  return (
                    <motion.span
                      key={i}
                      className="absolute w-1.5 h-1.5 rounded-full bg-[#7C9374]"
                      initial={{ x: 0, y: 0, opacity: 1 }}
                      animate={{ x: Math.cos(angle) * 84, y: Math.sin(angle) * 84, opacity: 0 }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Message de statut (fondu + léger glissement, sans flou) */}
          <div className="text-center mt-7 w-full">
            <h2 className="text-lg font-black text-[#2B4360] mb-1 leading-tight">
              {hasFailed ? 'Oups, un souci' : isCompleted ? 'Traitement finalisé' : 'Veuillez patienter…'}
            </h2>
            <div className="relative h-10 flex items-center justify-center">
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={stageMessage}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="text-sm font-medium text-slate-500 max-w-[240px] mx-auto leading-relaxed"
                >
                  {stageMessage}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Timeline verticale */}
        <div className="relative pl-2">
          <div className="absolute left-[17px] top-4 bottom-6 w-0.5 bg-slate-100"></div>
          <motion.div
            className={`absolute left-[17px] top-4 w-0.5 origin-top ${hasFailed ? 'bg-red-300' : isCompleted ? 'bg-[#7C9374]' : 'bg-[#4A90E2]'}`}
            style={{ height: '100%' }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: isCompleted ? 1 : currentStep === 1 ? 0 : 0.5 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />

          <div className="space-y-6 relative">
            {steps.map((step) => {
              const isActive = !isCompleted && !hasFailed && currentStep === step.id;
              const isStepCompleted = isCompleted || currentStep > step.id;

              return (
                <div key={step.id} className="flex gap-4 relative z-10 items-start">
                  <div className="shrink-0 mt-0.5 bg-white py-1">
                    <div className={`w-[20px] h-[20px] rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isStepCompleted
                        ? 'border-2 border-[#7C9374] bg-[#7C9374]'
                        : isActive
                          ? 'border-[3px] border-[#4A90E2] bg-white'
                          : 'border-2 border-slate-200 bg-white'
                    }`}>
                      {isStepCompleted ? (
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      ) : isActive ? (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4A90E2] animate-pulse" />
                      ) : null}
                    </div>
                  </div>

                  <div className="pt-0.5">
                    <p className={`text-sm font-bold transition-colors duration-300 ${
                      isStepCompleted ? 'text-[#2B4360]' : isActive ? 'text-[#4A90E2]' : 'text-slate-400'
                    }`}>
                      {step.name}
                    </p>
                    <p className={`text-xs mt-0.5 font-medium transition-colors duration-300 ${
                      isActive ? 'text-slate-500' : 'text-slate-400/60'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Loading;